import { FormBlock, FormTheme, DEFAULT_FORM_THEME } from './types'

export function generateFormHtml(
  blocks: FormBlock[],
  theme: FormTheme = DEFAULT_FORM_THEME,
): { html: string; css: string } {
  const lines: string[] = []
  lines.push('<form class="fb-form" novalidate>')

  let inHalfRow = false

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    const nextBlock = blocks[i + 1]
    const isHalf = block.width === 'half'
    const nextIsHalf = nextBlock?.width === 'half'

    if (isHalf && !inHalfRow) {
      lines.push('  <div class="fb-row">')
      inHalfRow = true
    }

    lines.push(renderBlock(block))

    if (inHalfRow && (!nextIsHalf || !nextBlock)) {
      lines.push('  </div>')
      inHalfRow = false
    }
  }

  lines.push('</form>')

  const html = lines.join('\n')
  const css = generateFormCss(theme)

  return { html, css }
}

function renderBlock(block: FormBlock): string {
  const indent = block.width === 'half' ? '    ' : '  '
  const wrapClass = block.width === 'half' ? 'fb-field fb-half' : 'fb-field'

  switch (block.type) {
    case 'text':
    case 'email':
    case 'phone':
    case 'number':
    case 'url':
    case 'date':
      return renderInput(block, indent, wrapClass)
    case 'textarea':
      return renderTextarea(block, indent, wrapClass)
    case 'select':
      return renderSelect(block, indent, wrapClass)
    case 'radio':
      return renderRadioGroup(block, indent, wrapClass)
    case 'checkbox':
      return renderCheckboxGroup(block, indent, wrapClass)
    case 'consent':
      return renderConsent(block, indent, wrapClass)
    case 'file':
      return renderFileInput(block, indent, wrapClass)
    case 'hidden':
      return `${indent}<input type="hidden" name="${esc(block.name)}" value="${esc(block.defaultValue)}" />`
    case 'heading':
      const tag = `h${block.level || 2}`
      return `${indent}<${tag} class="fb-heading">${esc(block.content || '')}</${tag}>`
    case 'paragraph':
      return `${indent}<p class="fb-paragraph">${esc(block.content || '')}</p>`
    case 'divider':
      return `${indent}<hr class="fb-divider" />`
    case 'spacer':
      return `${indent}<div class="fb-spacer" style="height:${block.height || 24}px"></div>`
    case 'image':
      if (!block.src)
        return `${indent}<div class="fb-image-placeholder">Image</div>`
      return `${indent}<img src="${esc(block.src)}" alt="${esc(block.alt || '')}" class="fb-image" />`
    case 'submit':
      const align = block.buttonAlign || 'full'
      const btnColorStyle = block.buttonColor
        ? ` style="background:${esc(block.buttonColor)};border-color:${esc(block.buttonColor)};"`
        : ''
      return `${indent}<div class="fb-submit fb-submit-${align}"><button type="submit" class="fb-button"${btnColorStyle}>${esc(block.buttonText || 'Submit')}</button></div>`
    default:
      return ''
  }
}

function renderInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const typeMap: Record<string, string> = {
    text: 'text',
    email: 'email',
    phone: 'tel',
    number: 'number',
    url: 'url',
    date: 'date',
  }
  const inputType = typeMap[block.type] || 'text'
  const attrs: string[] = [
    `type="${inputType}"`,
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.defaultValue) attrs.push(`value="${esc(block.defaultValue)}"`)
  if (block.validation.minLength)
    attrs.push(`minlength="${block.validation.minLength}"`)
  if (block.validation.maxLength)
    attrs.push(`maxlength="${block.validation.maxLength}"`)
  if (block.validation.min !== undefined)
    attrs.push(`min="${block.validation.min}"`)
  if (block.validation.max !== undefined)
    attrs.push(`max="${block.validation.max}"`)
  if (block.validation.pattern)
    attrs.push(`pattern="${esc(block.validation.pattern)}"`)

  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <input ${attrs.join(' ')} class="fb-input" />`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderTextarea(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const attrs: string[] = [
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.validation.minLength)
    attrs.push(`minlength="${block.validation.minLength}"`)
  if (block.validation.maxLength)
    attrs.push(`maxlength="${block.validation.maxLength}"`)

  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <textarea ${attrs.join(' ')} rows="4" class="fb-input fb-textarea">${esc(block.defaultValue)}</textarea>`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderSelect(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const options = block.validation.options || []
  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <select name="${esc(block.name)}" id="${esc(block.name)}" class="fb-input fb-select"${block.required ? ' required' : ''}>`,
    `${indent}    <option value="">${block.placeholder || 'Select...'}</option>`,
  ]
  for (const opt of options) {
    lines.push(
      `${indent}    <option value="${esc(opt.value)}">${esc(opt.label)}</option>`,
    )
  }
  lines.push(`${indent}  </select>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderRadioGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const options = block.validation.options || []
  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <fieldset class="fb-fieldset">`,
    `${indent}    <legend class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</legend>`,
  ]
  for (const opt of options) {
    const optId = `${block.name}_${opt.value}`
    lines.push(
      `${indent}    <label class="fb-option"><input type="radio" name="${esc(block.name)}" value="${esc(opt.value)}" id="${esc(optId)}"${block.required ? ' required' : ''} /> ${esc(opt.label)}</label>`,
    )
  }
  lines.push(`${indent}  </fieldset>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderCheckboxGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const options = block.validation.options || []
  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <fieldset class="fb-fieldset">`,
    `${indent}    <legend class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</legend>`,
  ]
  for (const opt of options) {
    const optId = `${block.name}_${opt.value}`
    lines.push(
      `${indent}    <label class="fb-option"><input type="checkbox" name="${esc(block.name)}" value="${esc(opt.value)}" id="${esc(optId)}" /> ${esc(opt.label)}</label>`,
    )
  }
  lines.push(`${indent}  </fieldset>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderConsent(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const text = block.consentText || block.label || 'I agree'
  const lines = [
    `${indent}<div class="${wrapClass} fb-consent">`,
    `${indent}  <label class="fb-option fb-consent-label">`,
    `${indent}    <input type="checkbox" name="${esc(block.name)}" value="yes"${block.required ? ' required' : ''} />`,
    `${indent}    <span class="fb-consent-text">${esc(text)}${block.required ? ' <span class="fb-required">*</span>' : ''}</span>`,
    `${indent}  </label>`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderFileInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
): string {
  const attrs: string[] = [
    `type="file"`,
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.required) attrs.push('required')
  if (block.validation.accept)
    attrs.push(`accept="${esc(block.validation.accept)}"`)

  const lines = [
    `${indent}<div class="${wrapClass}">`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <input ${attrs.join(' ')} class="fb-input fb-file" />`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  if (block.validation.maxFileSize)
    lines.push(
      `${indent}  <span class="fb-help">Max file size: ${block.validation.maxFileSize}MB</span>`,
    )
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function shadowValue(size: 'none' | 'sm' | 'md' | 'lg'): string {
  switch (size) {
    case 'sm':
      return '0 1px 2px 0 rgba(0,0,0,0.05)'
    case 'md':
      return '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)'
    case 'lg':
      return '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
    default:
      return 'none'
  }
}

function generateFormCss(theme: FormTheme): string {
  const bg =
    theme.backgroundType === 'gradient'
      ? `linear-gradient(${theme.gradientDirection}, ${theme.gradientFrom}, ${theme.gradientTo})`
      : theme.backgroundColor

  const containerShadow = shadowValue(theme.containerShadow)
  const containerBorderCss = theme.containerBorder
    ? `border: 1px solid ${theme.inputBorderColor};`
    : ''
  const inputShadowCss = theme.inputShadow
    ? 'box-shadow: 0 1px 3px 0 rgba(0,0,0,0.08);'
    : ''
  const buttonShadowCss = theme.buttonShadow
    ? 'box-shadow: 0 4px 14px 0 rgba(0,0,0,0.15);'
    : ''
  const focusColor = theme.inputFocusColor || theme.primaryColor

  return `.fb-form {
  font-family: ${theme.fontFamily};
  max-width: 640px;
  margin: 0 auto;
  padding: ${theme.formPadding || 32}px;
  background: ${bg};
  border-radius: ${theme.borderRadius + 4}px;
  box-shadow: ${containerShadow};
  ${containerBorderCss}
}
.fb-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 480px) {
  .fb-row { grid-template-columns: 1fr; }
}
.fb-field {
  margin-bottom: ${theme.fieldSpacing || 20}px;
}
.fb-label {
  display: block;
  font-size: ${(theme.labelFontSize || 14) / 16}rem;
  font-weight: ${theme.labelFontWeight || '500'};
  color: ${theme.labelColor};
  margin-bottom: 0.375rem;
}
.fb-required {
  color: #ef4444;
}
.fb-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #111827;
  background: ${theme.inputBackground || '#fff'};
  border: 1px solid ${theme.inputBorderColor};
  border-radius: ${theme.borderRadius}px;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  ${inputShadowCss}
}
.fb-input:focus {
  outline: none;
  border-color: ${focusColor};
  box-shadow: 0 0 0 3px ${focusColor}22;
}
.fb-textarea {
  resize: vertical;
  min-height: 100px;
}
.fb-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}
.fb-fieldset {
  border: none;
  padding: 0;
  margin: 0;
}
.fb-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  padding: 0.25rem 0;
  cursor: pointer;
}
.fb-option input {
  accent-color: ${theme.primaryColor};
}
.fb-consent-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
}
.fb-consent-label input[type="checkbox"] {
  margin-top: 0.2rem;
  accent-color: ${theme.primaryColor};
  flex-shrink: 0;
}
.fb-consent-text {
  font-size: 0.875rem;
  color: ${theme.labelColor};
  line-height: 1.4;
}
.fb-help {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.25rem;
}
.fb-heading {
  color: ${theme.headingColor || '#111827'};
  margin: 0 0 0.5rem;
}
.fb-paragraph {
  color: ${theme.paragraphColor || '#6b7280'};
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0 0 1rem;
}
.fb-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
}
.fb-spacer {
  display: block;
}
.fb-image {
  max-width: 100%;
  height: auto;
  border-radius: ${theme.borderRadius}px;
}
.fb-image-placeholder {
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: ${theme.borderRadius}px;
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}
.fb-submit {
  margin-top: 1.5rem;
}
.fb-submit-center { text-align: center; }
.fb-submit-right { text-align: right; }
.fb-submit-full .fb-button { width: 100%; }
.fb-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: ${theme.borderRadius}px;
  cursor: pointer;
  transition: background-color 0.15s, opacity 0.15s, transform 0.1s;
  border: ${theme.buttonStyle === 'outline' ? `2px solid ${theme.primaryColor}` : 'none'};
  background: ${theme.buttonStyle === 'outline' ? 'transparent' : theme.primaryColor};
  color: ${theme.buttonStyle === 'outline' ? theme.primaryColor : theme.buttonTextColor || '#ffffff'};
  ${buttonShadowCss}
}
.fb-button:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.fb-button:active {
  transform: translateY(0);
}
.fb-file {
  padding: 0.375rem;
}
.fb-file::file-selector-button {
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.borderRadius}px;
  border: 1px solid ${theme.inputBorderColor};
  background: #f9fafb;
  font-size: 0.8125rem;
  cursor: pointer;
  margin-right: 0.75rem;
}`
}

function esc(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
